<template>
  <div class="letter-catch">
    <LevelComplete v-if="isGameComplete" message="✅ Excellent!" />
    <div v-if="!isGameComplete">
      <h1 :class="['target-letter', { highlight: highlightTarget }]" @click="
        selectedCategory === 'numbers' && targetFruit
          ? speakOutLoud(`${targetNumber} ${targetFruit.emoji}`)
          : selectedCategory !== 'numbers'
            ? speakOutLoud(targetLetter)
            : null
        ">
        <template v-if="selectedCategory !== 'numbers'">
          {{ targetLetter }}
        </template>
        <template v-else-if="targetFruit">
          {{ targetNumber }} {{ targetFruit.emoji }}
        </template>
        <template v-else>
          Loading...
        </template>
      </h1>
      <p v-if="message" class="message">
        {{ message }}
      </p>
      <div class="game-area" @mousemove="moveCatcher" @touchmove="moveCatcher" @touchstart.prevent @touchend.prevent>
        <div v-for="(item, index) in fallingItems" :key="index" :style="{ top: `${item.y}px`, left: `${item.x}%` }"
          class="falling-item">
          {{ item.char }}
        </div>
        <div ref="catcher" class="catcher" />
      </div>
      <div class="progress">
        <template v-if="selectedCategory === 'numbers'">
          <p class="progress-number">
            {{ targetFruit?.emoji }} caught: {{ fruitsCaught }}/{{ targetNumber }}
          </p>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { speechMixin } from "@/mixins/speechMixin.js";

import LevelComplete from "../LevelComplete.vue";

export default {
  components: {
    LevelComplete,
  },
  mixins: [speechMixin],
  props: {
    selectedCategory: {
      type: String,
      default: "vowels",
    },
  },
  data() {
    const fruits = [
      { emoji: "🍎", name: "apple" },
      { emoji: "🍌", name: "banana" },
      { emoji: "🍇", name: "grape" },
      { emoji: "🍓", name: "strawberry" },
      { emoji: "🍉", name: "watermelon" },
      { emoji: "🍊", name: "orange" },
      { emoji: "🍍", name: "pineapple" },
      { emoji: "🥝", name: "kiwi" },
      { emoji: "🍒", name: "cherry" },
      { emoji: "🍐", name: "pear" },
    ];

    return {
      categories: {
        vowels: ["A", "E", "I", "O", "U"],
        alphabet: "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split(""),
        numbers: "0123456789".split(""),
      },
      targetLetter: "",
      targetNumber: 0,
      targetFruit: fruits[0], // Initialize with default fruit
      message: "",
      letterCounts: {},
      maxCount: 1,
      selectedVoice: null,
      fallingItems: [],
      intervalId: null,
      maxFallingLetters: 5,
      fallingSpeed: 500,
      letterFallIncrement: 25,
      highlightTarget: false,
      isGameComplete: false,
      fruits: fruits,
      fruitsCaught: 0,
      roundsCompleted: 0,
      maxRounds: 5,
      catchingDisabled: false, // New flag to disable catching temporarily
    };
  },
  computed: {
    currentCategory() {
      return this.categories[this.selectedCategory];
    },
  },
  mounted() {
    this.setupVoices();
    this.initializeLetterCounts();
    this.setupGame();
    this.startFallingItems();

    // Set initial position of catcher to center
    this.$nextTick(() => {
      const catcher = this.$refs.catcher;
      const gameAreaRect = this.$el
        .querySelector(".game-area")
        .getBoundingClientRect();
      const catcherX = (gameAreaRect.width - catcher.offsetWidth) / 2;
      catcher.style.left = `${catcherX}px`;
    });
  },
  beforeUnmount() {
    clearInterval(this.intervalId);
  },
  methods: {
    initializeLetterCounts() {
      if (this.selectedCategory !== "numbers") {
        this.letterCounts = this.currentCategory.reduce((acc, letter) => {
          acc[letter] = 0;
          return acc;
        }, {});
      }
    },
    setupGame() {
      if (this.selectedCategory === "numbers") {
        if (this.roundsCompleted >= this.maxRounds) {
          // Game complete logic
          this.playSound(
            "https://codeskulptor-demos.commondatastorage.googleapis.com/descent/gotitem.mp3"
          );
          this.speakOutLoud("Excellent!");
          this.isGameComplete = true;

          setTimeout(() => {
            this.$router.push("/numbers");
          }, 4000);
          return;
        }

        this.roundsCompleted++;

        // Randomly select a target number and a target fruit
        this.targetNumber = Math.floor(Math.random() * 5) + 1; // Random number between 1 and 5
        this.targetFruit = this.fruits[Math.floor(Math.random() * this.fruits.length)];

        const plural = this.targetNumber > 1 ? 's' : '';
        this.speakOutLoud(`Catch ${this.targetNumber} ${this.targetFruit.name}${plural}!`);
        this.message = "";

        this.fruitsCaught = 0; // Reset fruits caught
      } else {
        const availableLetters = this.currentCategory.filter(
          (letter) => this.letterCounts[letter] < this.maxCount
        );

        if (availableLetters.length === 0) {
          this.playSound(
            "https://codeskulptor-demos.commondatastorage.googleapis.com/descent/gotitem.mp3"
          );
          this.speakOutLoud("Excellent!");
          this.isGameComplete = true;

          setTimeout(() => {
            this.$router.push("/letters");
          }, 4000);
          return;
        }

        this.targetLetter =
          availableLetters[Math.floor(Math.random() * availableLetters.length)];
        this.speakOutLoud(`Catch the letter "${this.targetLetter}"`);
        this.message = "";
      }
    },
    startFallingItems() {
      this.intervalId = setInterval(() => {
        if (this.selectedCategory === "numbers") {
          // First, ensure the target fruit is present
          const targetFruitInItems = this.fallingItems.some(
            (item) => item.fruit && item.fruit.name === this.targetFruit.name
          );

          if (!targetFruitInItems && this.fallingItems.length < this.maxFallingLetters) {
            const item = {
              char: this.targetFruit.emoji,
              fruit: this.targetFruit,
              isFruit: true,
              x: this.getRandomPosition(), // Use new spaced-out x-position
              y: 0,
            };
            this.fallingItems.push(item);
          }

          // Then, add other fruits with proper spacing
          while (this.fallingItems.length < this.maxFallingLetters) {
            const item = this.getRandomItem();
            item.x = this.getRandomPosition(); // Ensure no overlap
            item.y = 0;
            this.fallingItems.push(item);
          }
        } else {
          // First, ensure target letter is present
          const targetLetterInItems = this.fallingItems.some(
            (item) => item.char === this.targetLetter
          );

          if (!targetLetterInItems && this.fallingItems.length < this.maxFallingLetters) {
            const item = {
              char: this.targetLetter,
              isFruit: false,
              x: this.getRandomPosition(), // Use new spaced-out x-position
              y: 0,
            };
            this.fallingItems.push(item);
          }

          // Then, add other items with proper spacing
          while (this.fallingItems.length < this.maxFallingLetters) {
            const item = this.getRandomItem();
            item.x = this.getRandomPosition(); // Ensure no overlap
            item.y = 0;
            this.fallingItems.push(item);
          }
        }

        this.updateFallingItems();
      }, this.fallingSpeed);
    },
    getRandomItem() {
      if (this.selectedCategory === "numbers") {
        // Return a random fruit
        const randomFruit = this.fruits[Math.floor(Math.random() * this.fruits.length)];
        return {
          char: randomFruit.emoji,
          fruit: randomFruit,
          isFruit: true
        };
      } else {
        // Return a random letter
        const lettersInFallingItems = this.fallingItems.map(
          (item) => item.char
        );
        const availableLetters = this.currentCategory.filter(
          (letter) =>
            !lettersInFallingItems.includes(letter) &&
            letter !== this.targetLetter
        );

        if (availableLetters.length === 0) {
          // All letters are used, reset availableLetters
          availableLetters.push(
            ...this.currentCategory.filter(
              (letter) => letter !== this.targetLetter
            )
          );
        }

        let randomLetter =
          availableLetters[Math.floor(Math.random() * availableLetters.length)];

        return { char: randomLetter, isFruit: false };
      }
    },
    getRandomPosition() {
      // Set the total space between items on the X-axis
      const margin = 10; // Minimum space between items (in percentage of screen width)
      let position = Math.floor(Math.random() * (100 - margin)); // Ensures a margin for the last item
      if (this.fallingItems.length === 0) return position; // First item, no conflict

      // Avoid placing items too close together
      const minDistance = 10; // Minimum distance in percentage between items
      for (let i = 0; i < this.fallingItems.length; i++) {
        const otherItem = this.fallingItems[i];
        if (Math.abs(otherItem.x - position) < minDistance) {
          position = this.getRandomPosition(); // Try again if too close to another item
          break;
        }
      }

      return position;
    },
    updateFallingItems() {
      this.fallingItems.forEach((item, index) => {
        item.y += this.letterFallIncrement;
        if (item.y > 400) {
          this.fallingItems.splice(index, 1);
        }
      });
      this.checkCatch();
    },
    checkCatch() {
      const catcher = this.$refs.catcher;
      if (!catcher || this.catchingDisabled) return;

      this.fallingItems.forEach((item, index) => {
        const itemEl = this.$el.querySelectorAll(".falling-item")[index];
        if (itemEl) {
          const itemRect = itemEl.getBoundingClientRect();
          const catcherRect = catcher.getBoundingClientRect();

          if (
            itemRect.bottom >= catcherRect.top &&
            itemRect.top <= catcherRect.bottom &&
            itemRect.left <= catcherRect.right &&
            itemRect.right >= catcherRect.left
          ) {
            if (this.selectedCategory === "numbers") {
              if (item.isFruit && item.fruit.name === this.targetFruit.name) {
                this.playSound(
                  "https://commondatastorage.googleapis.com/codeskulptor-assets/week7-brrring.m4a"
                );
                this.fruitsCaught++;
                this.fallingItems.splice(index, 1);

                if (this.fruitsCaught >= this.targetNumber) {
                  // Move to next number and fruit
                  this.highlightTarget = true;
                  this.catchingDisabled = true; // Disable catching
                  setTimeout(() => {
                    this.highlightTarget = false;
                    this.catchingDisabled = false; // Re-enable catching
                    this.setupGame();
                  }, 1000); // Lock for 1 second
                }
              } else {
                // Remove the item anyway
                this.fallingItems.splice(index, 1);
              }
            } else {
              if (item.char === this.targetLetter) {
                this.playSound(
                  "https://commondatastorage.googleapis.com/codeskulptor-assets/week7-brrring.m4a"
                );
                this.speakOutLoud(`${this.targetLetter}`);
                this.highlightTarget = true;
                this.letterCounts[this.targetLetter]++;
                this.fallingItems.splice(index, 1);

                setTimeout(() => {
                  this.highlightTarget = false;
                  if (this.letterCounts[this.targetLetter] >= this.maxCount) {
                    this.setupGame();
                  }
                }, 3000);
              }
            }
          }
        }
      });
    },
    resetGame() {
      this.fallingItems = [];
      this.highlightTarget = false; // Ensure highlight is reset
      this.setupGame();
      this.startFallingItems(); // Restart falling items
    },
    moveCatcher(event) {
      let clientX;
      if (event.type === "mousemove") {
        clientX = event.clientX;
      } else if (event.type === "touchmove") {
        event.preventDefault();
        clientX = event.touches[0].clientX;
      }

      const catcher = this.$refs.catcher;
      if (catcher) {
        const gameAreaRect = this.$el
          .querySelector(".game-area")
          .getBoundingClientRect();
        let catcherX = clientX - gameAreaRect.left - catcher.offsetWidth / 2;

        // Ensure the catcher stays within the game area
        catcherX = Math.max(
          0,
          Math.min(catcherX, gameAreaRect.width - catcher.offsetWidth)
        );

        catcher.style.left = `${catcherX}px`;
      }
    },
    playSound(url) {
      const audio = new Audio(url);
      audio.play();
    },
  },
};
</script>

<style scoped>
.progress {
  text-align: center;
  font-size: 1.5rem;
  margin-bottom: 20px;
}

.game-area {
  position: relative;
  height: 400px;
  border: 2px solid #ccc;
  overflow: hidden;
  margin-bottom: 30px;
  touch-action: none;
  /* Prevent mobile zooming and scrolling */
}

.target-letter {
  max-width: 100px;
  margin: auto;
  margin-bottom: 20px;
  font-size: 4rem;
  color: #ff4081;
  text-align: center;
  background-color: lightgray;
  border-radius: 10px;
  border: 8px solid gray;
  cursor: pointer;
  transition: all 0.3s;
}

.target-letter.highlight {
  background-color: lightgreen;
  border: 8px solid green;
  transform: scale(1.2);
}

.falling-item {
  position: absolute;
  font-size: 2rem;
  background-color: #81d4fa;
  padding: 10px;
  border-radius: 200px;
  height: 50px;
  width: 50px;
  font-weight: bold;
  text-align: center;
  box-shadow: 2px 2px 2px 2px gray;
}

.catcher {
  position: absolute;
  bottom: 10px;
  left: 0;
  width: 100px;
  height: 20px;
  background-color: lightgreen;
  border-radius: 5px;
  box-shadow: 2px 2px 2px 2px gray;
}

.message {
  font-size: 3rem;
  font-weight: bold;
  text-align: center;
}

.progress-number {
  font-size: 2rem;
}

.success-illustration {
  max-width: 400px;
}

/* For mobile devices to prevent default touch behavior */
.game-area {
  touch-action: none;
}
</style>
